// Wooden-boat GLB loader + trimesh extractor for the "Sinking Ship" map.
//
// Mirrors the SpaceshipAssets pipeline so the visible hull and the
// physics collision are baked from the SAME geometry — exactly the
// same scale, centre, and rotation pass on both layers, so a player
// standing on the deck never sees the visual and the collider drift
// apart.
//
// Layer split:
//   * Browser path (this file)   — loads the GLB via GLTFLoader, scales
//     and centres the template, traverses meshes for shadow flags +
//     PBR tweaks, and bakes a trimesh in-memory as a fallback.
//   * Server / sim path          — see boatCollision.ts: loads the
//     committed JSON written by `scripts/bake-boat-collision.mjs`. Both
//     server (fs) and client (fetch) end up with bit-identical vertex
//     data so character ↔ deck contact agrees across the wire.

import * as THREE from 'three';
import { createGLTFLoader } from './glbLoader.js';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { preloadBoatCollision } from '../maps/bundles/sinking-ship/boatCollision';
import SubmarineHullMaterial from '../maps/bundles/sinking-ship/materials/SubmarineHullMaterial';

// World-space tile size for the box-projected UVs we bake onto the submarine
// hull. The PBR maps in /materials/submarine repeat once per this many metres
// across whichever pair of world axes a vertex's normal projects onto.
const SUB_TILE_METRES = 8.0;

// TEMPORARY: submarine swap for the sinking-ship map. Revert to
// '/models/world/wooden-boat.glb' to restore the original. Collision
// data is being read from sub-collision.json (see boatCollision.ts).
const MODEL_PATH = '/models/world/submarine.glb';

// Target world-space size of the boat's longest bbox dimension.
// TEMPORARY: bumped 15.0 → 30.0 for the submarine test (2× scale). The
// matching constant in bake-sub-collision.mjs is also 30.0 so visual and
// collider stay in lockstep. Revert to 15.0 (and bake-boat-collision.mjs
// stays at 15.0) when restoring the wooden boat.
const BOAT_TARGET_MAX_DIM = 30.0;
// GLB models often face a non-default direction. Set to 0 unless the
// model needs a heading correction; the bake script uses the same value.
const BOAT_YAW_OFFSET_DEG = 0;

export interface BoatCollisionMesh {
  vertices: Float32Array;
  indices: Uint32Array;
}

export interface WoodenBoatAssets {
  /** Scaled, centred boat mesh group. Cloned by the behavior into its
   *  visual ship parent so the template stays pristine. */
  modelTemplate: THREE.Group;
  /** Trimesh data baked from the same scaled+centred mesh; the in-memory
   *  fallback for single-player. The committed JSON wins on the
   *  authoritative path so server and client agree vertex-for-vertex. */
  collision: BoatCollisionMesh;
  /** World-space size of the boat's longest axis (preview camera framing). */
  maxDim: number;
  /** World-space bbox of the scaled+centred model. */
  bbox: THREE.Box3;
  /** Triangle count of the baked collider. */
  colliderTriCount: number;
}

let _loadPromise: Promise<WoodenBoatAssets> | null = null;
let _loaded: WoodenBoatAssets | null = null;

/** Idempotent preload. Call from main.ts before constructing the Renderer
 *  for any flow that lands on the sinking-ship map. */
export function preloadWoodenBoatAssets(): Promise<WoodenBoatAssets> {
  if (_loadPromise) return _loadPromise;
  _loadPromise = loadImpl();
  return _loadPromise;
}

export function getWoodenBoatAssets(): WoodenBoatAssets | null {
  return _loaded;
}

async function loadImpl(): Promise<WoodenBoatAssets> {
  // Prime the committed collision JSON in parallel with the GLB load.
  // The sim path (single-player local arena, multiplayer follower's
  // local World, server) consumes that JSON — by awaiting it here we
  // guarantee one preloadWoodenBoatAssets() leaves both layers ready.
  const collisionPromise = preloadBoatCollision();

  const raw = await new Promise<THREE.Group>((resolve, reject) => {
    createGLTFLoader().load(
      MODEL_PATH,
      (gltf) => resolve(gltf.scene),
      undefined,
      (err) => reject(err)
    );
  });

  // Apply the yaw offset to the raw GLB up-front so visual and collider
  // are extracted from the same orientation. With the trimesh derived
  // from this exact transform, rotating once here keeps everything in
  // lockstep.
  raw.rotation.y = BOAT_YAW_OFFSET_DEG * Math.PI / 180;

  // Scale so the longest bbox axis matches the target. Works regardless
  // of what units the modelling tool exported in.
  raw.updateMatrixWorld(true);
  const rawBbox = new THREE.Box3().setFromObject(raw);
  const rawSize = rawBbox.getSize(new THREE.Vector3());
  const rawMax = Math.max(rawSize.x, rawSize.y, rawSize.z);
  const scale = rawMax > 1e-6 ? BOAT_TARGET_MAX_DIM / rawMax : 1;

  const template = new THREE.Group();
  template.add(raw);
  template.scale.setScalar(scale);
  template.updateMatrixWorld(true);

  // Centre on origin in XZ. Y is preserved as the artist authored it,
  // so spawns + sink offsets stay in the model's local frame. (Unlike
  // the spaceship which centres on top: a wooden boat has a tall mast
  // above the deck so anchoring on the topmost point would push the
  // hull underwater.)
  const scaledBbox = new THREE.Box3().setFromObject(template);
  const scaledCentre = scaledBbox.getCenter(new THREE.Vector3());
  template.position.x -= scaledCentre.x;
  template.position.z -= scaledCentre.z;
  template.updateMatrixWorld(true);

  // Strip the GLB's baked materials and UVs and re-wrap every mesh in a
  // single shared PBR submarine hull material, mirroring what the airship
  // map does for its deck plates. The submarine GLB ships with whatever
  // textures/normals/UV layout the source authored — none of that survives;
  // we generate fresh world-space box-projected UVs (so the new maps tile
  // consistently across the curved hull) and bind one MeshStandardMaterial
  // instance to every mesh.
  const hullMaterial = new SubmarineHullMaterial();
  template.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!(mesh as { isMesh?: boolean }).isMesh) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    const oldMats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of oldMats) {
      disposeMaterial(mat as THREE.Material | null | undefined);
    }

    applyBoxProjectionUVs(mesh, SUB_TILE_METRES);
    mesh.material = hullMaterial;
  });

  const bbox = new THREE.Box3().setFromObject(template);
  const size = bbox.getSize(new THREE.Vector3());

  // Wait for the JSON-baked collision before claiming we're ready. The
  // sim init reads it synchronously via getBoatCollision(); the in-memory
  // bake is the SAME shape and acts as the local-fallback if the JSON
  // is ever missing during dev.
  await collisionPromise;
  const collision = bakeTrimesh(template);

  _loaded = {
    modelTemplate: template,
    collision,
    maxDim: Math.max(size.x, size.y, size.z),
    bbox,
    colliderTriCount: Math.round(collision.indices.length / 3)
  };
  return _loaded;
}

/** Per-vertex box projection: pick the dominant world-space normal axis
 *  and project the vertex's other two world-space coords as UVs, divided
 *  by `tileMetres` so the texture repeats once per tile. Replaces any
 *  existing UVs the GLB shipped with. Geometry attributes (position,
 *  index, normal) are preserved so the trimesh bake still sees the same
 *  vertices it always did. */
function applyBoxProjectionUVs(mesh: THREE.Mesh, tileMetres: number): void {
  const geom = mesh.geometry as THREE.BufferGeometry;
  const posAttr = geom.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!posAttr) return;

  if (!geom.getAttribute('normal')) geom.computeVertexNormals();
  const normAttr = geom.getAttribute('normal') as THREE.BufferAttribute;

  mesh.updateMatrixWorld(true);
  const matrix = mesh.matrixWorld;
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrix);
  const p = new THREE.Vector3();
  const n = new THREE.Vector3();

  const uvs = new Float32Array(posAttr.count * 2);
  const inv = 1 / tileMetres;
  for (let i = 0; i < posAttr.count; i++) {
    p.fromBufferAttribute(posAttr, i).applyMatrix4(matrix);
    n.fromBufferAttribute(normAttr, i).applyMatrix3(normalMatrix).normalize();
    const ax = Math.abs(n.x);
    const ay = Math.abs(n.y);
    const az = Math.abs(n.z);
    let u: number;
    let v: number;
    if (ax >= ay && ax >= az) {
      // Side of hull (normal facing X) — project ZY plane.
      u = p.z; v = p.y;
    } else if (ay >= az) {
      // Top/bottom (normal facing Y) — project XZ plane.
      u = p.x; v = p.z;
    } else {
      // Bow/stern (normal facing Z) — project XY plane.
      u = p.x; v = p.y;
    }
    uvs[i * 2] = u * inv;
    uvs[i * 2 + 1] = v * inv;
  }

  geom.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  // A second UV channel is needed by MeshStandardMaterial only when an
  // aoMap is present; we don't supply one, so uv2 is left untouched.
}

function disposeMaterial(mat: THREE.Material | null | undefined): void {
  if (!mat) return;
  const m = mat as THREE.MeshStandardMaterial & Record<string, unknown>;
  for (const key of [
    'map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap',
    'emissiveMap', 'bumpMap', 'displacementMap', 'alphaMap', 'envMap'
  ]) {
    const tex = m[key] as THREE.Texture | null | undefined;
    if (tex && typeof tex.dispose === 'function') tex.dispose();
  }
  mat.dispose();
}

/** Walk the template's scene graph and merge every visible mesh's
 *  geometry into a single (vertices, indices) pair in world space. The
 *  result feeds straight into `ColliderDesc.trimesh`. */
function bakeTrimesh(group: THREE.Group): BoatCollisionMesh {
  const vertexList: number[] = [];
  const indexList: number[] = [];
  const tmp = new THREE.Vector3();

  group.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!(mesh as { isMesh?: boolean }).isMesh) return;

    let geom = mesh.geometry as THREE.BufferGeometry;
    if (!geom.index) geom = mergeVertices(geom);

    const posAttr = geom.getAttribute('position') as THREE.BufferAttribute | undefined;
    const idx = geom.index;
    if (!posAttr || !idx) return;

    const baseVertex = vertexList.length / 3;
    const matrix = mesh.matrixWorld;
    for (let i = 0; i < posAttr.count; i++) {
      tmp.fromBufferAttribute(posAttr, i).applyMatrix4(matrix);
      vertexList.push(tmp.x, tmp.y, tmp.z);
    }
    for (let i = 0; i < idx.count; i++) {
      indexList.push(idx.getX(i) + baseVertex);
    }
  });

  return {
    vertices: new Float32Array(vertexList),
    indices: new Uint32Array(indexList)
  };
}
